import React, { useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useAuthState } from '@vis-auth/react';
import { Account, AccountStatus, AccountType } from 'eve-account-client';
import { useHistory, useLocation } from 'react-router-dom';
import { EveRoutes } from '@ver-uds/e-verify';
import { useAccountQuery } from '../services';
import TerminationRequest from '../components/TerminationRequest';
import TerminationRequestSummary from '../components/TerminationRequestSummary/TerminationRequestSummary';
import TerminateCompanyLocations from '../components/TerminateCompanyLocations';
import TerminationRequestReview from '../components/TerminationRequestReview';
import TerminateCompanyLocationsReview from '../components/TerminateCompanyLocationsReview';

export type CloseAccountProps = {
  companyLocations?: boolean;
};

export type CloseAccountPageLocationState =
  | null
  | undefined
  | {
      selectedAccounts?: Account[];
    };

type FormValues = {
  closedReasonCode: string;
  closedReasonText: string;
  childClosedReasonCode: string;
  dunsNumber: string;
};

const CloseAccountPage = ({ companyLocations }: CloseAccountProps): JSX.Element => {
  const { user } = useAuthState();
  // eslint-disable-next-line camelcase
  const accountId = user?.account_id as string;

  const { data: accountData } = useAccountQuery(accountId);
  const accountType = accountData?.type || AccountType.CORPORATE;

  const [onReviewPage, setOnReviewPage] = useState(false);
  const [terminationFormValues, setTerminationFormValues] = useState<FormValues>({
    closedReasonCode: '',
    closedReasonText: '',
    childClosedReasonCode: '',
    dunsNumber: '',
  });
  const goToReviewPage = (formValues: FormValues): void => {
    setTerminationFormValues(formValues);
    setOnReviewPage(true);
  };
  const leaveReviewPage = (): void => {
    setOnReviewPage(false);
  };
  const { state } = useLocation<CloseAccountPageLocationState>();
  const selectedAccounts = state?.selectedAccounts ?? [];

  const manageCompaniesHomeLink =
    accountType === AccountType.AGENCY ? EveRoutes.COMPANY_CLIENTS_VIEW : EveRoutes.CORPORATE_COMPANY_LOCATIONS_VIEW;

  // this line prevents selectedAccounts from being populated after a page refresh
  window.history.replaceState({}, document.title);
  const history = useHistory();

  // if there are no more selectedAccounts for company locations, route the user back to the Manage Companies page
  if (companyLocations && selectedAccounts.length === 0) {
    history.push(manageCompaniesHomeLink);
  }
  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      pageTitle: 'E-Verify - Close Account Page',
    });
  }, []);

  if (accountData) {
    if (!companyLocations) {
      if (accountData?.status === AccountStatus.TERMINATIONREQUESTED) {
        return <TerminationRequestSummary accountData={accountData} accountType={accountType} />;
      }
      return onReviewPage ? (
        <TerminationRequestReview
          accountData={accountData}
          terminationFormValues={terminationFormValues}
          leaveReviewPage={leaveReviewPage}
        />
      ) : (
        <TerminationRequest
          accountData={accountData}
          terminationFormValues={terminationFormValues}
          goToReviewPage={goToReviewPage}
        />
      );
    }
    return onReviewPage || selectedAccounts[0]?.status === AccountStatus.TERMINATIONREQUESTED ? (
      <TerminateCompanyLocationsReview
        accountType={accountType}
        accountData={accountData}
        selectedAccounts={selectedAccounts}
        terminationFormValues={terminationFormValues}
        leaveReviewPage={leaveReviewPage}
      />
    ) : (
      <TerminateCompanyLocations
        accountType={accountType}
        selectedAccounts={selectedAccounts}
        terminationFormValues={terminationFormValues}
        goToReviewPage={goToReviewPage}
      />
    );
  }
  return <></>;
};

export default CloseAccountPage;
